import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router";
import { transformPluginsApi, TransformPluginRow } from "./api/transform-plugins-client";
import { AdminListHeader } from "../../../components/admin/AdminListHeader";
import { Drawer } from "@/components/Drawer";
import "../admin.css";

const FAMILIES = ["string", "literal", "cross-field", "logic", "lookup", "number", "date"] as const;

/**
 * F-034 — "+ New plugin" create dialog (the New Job Type pattern). The old CTA
 * linked to /admin/transform-plugins/new, which the detail page 404'd (it has
 * no create mode). pluginId/family/label + a minimal starter expression that
 * passes validatePluginExpression → POST → navigate to the new draft.
 */
function CreatePluginDialog({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  const [pluginId, setPluginId] = useState("");
  const [label, setLabel] = useState("");
  const [family, setFamily] = useState<(typeof FAMILIES)[number]>("string");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleCreate() {
    setSaving(true);
    setError(null);
    try {
      const row = await transformPluginsApi.create({
        pluginId,
        family,
        label,
        // `value` is the identity expression — the smallest body the
        // expression validator accepts; the author replaces it in the editor.
        expression: "value",
      });
      onCreated(row.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to create plugin");
      setSaving(false);
    }
  }

  return (
    <Drawer isOpen onClose={onClose} title="New transform plugin" width="sm"
      footer={
        <>
          <button type="button" className="rb-btn" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="rb-btn rb-btn-save"
            disabled={saving || !pluginId.trim() || !label.trim()}
            onClick={() => void handleCreate()}
          >
            Create
          </button>
        </>
      }
    >
      <div className="automation-editor__body">
        <label className="automation-editor__row">
          <span>Plugin id</span>
          <input
            type="text"
            value={pluginId}
            placeholder="snake_case_id"
            onChange={(e) => setPluginId(e.target.value)}
          />
        </label>
        <label className="automation-editor__row">
          <span>Label</span>
          <input type="text" value={label} placeholder="Human-readable name" onChange={(e) => setLabel(e.target.value)} />
        </label>
        <label className="automation-editor__row">
          <span>Family</span>
          <select value={family} onChange={(e) => setFamily(e.target.value as (typeof FAMILIES)[number])}>
            {FAMILIES.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
        </label>
        {error && <p className="admin-error" role="alert">{error}</p>}
      </div>
    </Drawer>
  );
}

function StatusBadge({ status }: { status: string }) {
  const colorMap: Record<string, React.CSSProperties> = {
    published: { color: "#4CAF50", borderColor: "#4CAF50" },
    draft: { color: "#FFC107", borderColor: "#FFC107" },
    archived: { color: "var(--color-text-muted)", borderColor: "var(--color-text-muted)" },
  };
  const style = colorMap[status] ?? colorMap["archived"];
  return (
    <span className="jt-badge" style={style}>
      {status}
    </span>
  );
}

export function TransformPluginListPage() {
  const navigate = useNavigate();
  const [plugins, setPlugins] = useState<TransformPluginRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    transformPluginsApi
      .list()
      .then((data) => {
        if (!cancelled) setPlugins(data.plugins);
      })
      .catch((err: unknown) => {
        if (!cancelled)
          setError(err instanceof Error ? err.message : "Failed to load plugins");
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="admin-page">
      <AdminListHeader
        title="Transform Plugins"
        count={plugins.length}
        primaryAction={{ label: "+ New plugin", onClick: () => setCreateOpen(true) }}
      />

      {createOpen && (
        <CreatePluginDialog
          onClose={() => setCreateOpen(false)}
          onCreated={(id) => navigate(`/admin/transform-plugins/${id}`)}
        />
      )}

      {isLoading && (
        <div className="admin-loading">
          <div className="loading-spinner" />
        </div>
      )}

      {error && <div className="admin-error">{error}</div>}

      {!isLoading && !error && plugins.length === 0 && (
        <div className="tab-placeholder">
          <span className="tab-placeholder-text">
            No transform plugins configured yet.
          </span>
          <button
            type="button"
            className="rb-btn rb-btn-save"
            style={{ marginTop: "var(--space-4)", display: "inline-block" }}
            onClick={() => setCreateOpen(true)}
          >
            + New plugin
          </button>
        </div>
      )}

      <div className="jt-list">
        {plugins.map((plugin) => (
          <div key={plugin.id} className="jt-card" style={{ cursor: "pointer" }}>
            <div className="jt-card-header">
              <Link
                to={`/admin/transform-plugins/${plugin.id}`}
                className="jt-card-name"
                style={{ textDecoration: "none", color: "inherit" }}
              >
                {plugin.label}
              </Link>
              <StatusBadge status={plugin.status} />
              <span className="jt-badge">{plugin.family}</span>
            </div>
            <div className="jt-card-meta">
              <span className="jt-card-plugin-id">{plugin.pluginId}</span>
              <span>&middot;</span>
              <span>Updated {new Date(plugin.updatedAt).toLocaleDateString()}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
